import React from 'react'
import { Box, Tooltip, Typography, alpha, useTheme } from '@mui/material'
import type { RowQueueModel } from '../../utils/pipelineRowQueue'

/**
 * The compact per-row queue strip: one bar plus one line of words.
 *
 * The bar is the share of the pipeline's records that are WAITING — uncoded
 * and owned by no live batch. It is deliberately not a progress bar: the feed
 * cannot support a coded count (see utils/pipelineRowQueue.ts), and drawing
 * "the rest" as success would report every record in flight as finished. The
 * remainder is therefore neutral, and batches in flight are named in words so
 * the bar never mixes two units on one axis.
 *
 * With no stored record total there is no denominator, so no bar is drawn.
 */
export interface PipelineQueueStripProps {
  model: RowQueueModel
}

const PipelineQueueStrip: React.FC<PipelineQueueStripProps> = ({ model }) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  // No feed, or a pipeline the feed does not cover (archived): words only.
  // Also words only when there is no record total to draw the bar against.
  if (!model.known || !model.totalKnown) {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {model.summary}
      </Typography>
    )
  }

  const total = Math.max(1, model.total)
  const waitingPct = Math.min(100, (model.waiting / total) * 100)
  const restPct = 100 - waitingPct

  // Held-back records read as the warning colour: they are back-pressure, not
  // progress. Failed records are the error colour — only a retry clears them.
  const waitingColor = model.failed > 0
    ? theme.palette.error.main
    : model.heldBy
      ? theme.palette.warning.main
      : theme.palette.info.main

  return (
    <Box sx={{ minWidth: 150 }}>
      <Tooltip title={model.summary}>
        {/* aria-hidden: the caption below IS this bar's text, and announcing
            both means every row is read out twice. */}
        <Box
          aria-hidden
          sx={{
            display: 'flex',
            height: 6,
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: alpha(theme.palette.text.primary, isDark ? 0.14 : 0.08),
          }}
        >
          {/* Waiting first — it is the part with a claim behind it. The
              remainder is NOT "coded" and is never coloured as success. */}
          <Box sx={{ width: `${waitingPct}%`, bgcolor: waitingColor }} />
          <Box sx={{ width: `${restPct}%`, bgcolor: 'transparent' }} />
        </Box>
      </Tooltip>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: 0.5,
          fontSize: '0.68rem',
          lineHeight: 1.3,
          color: model.heldBy || model.failed > 0 ? 'warning.main' : 'text.secondary',
        }}
      >
        {model.summary}
      </Typography>
    </Box>
  )
}

export default PipelineQueueStrip
